import { apiClient } from './api';
import type {
  CreateOneOnOneApiRequest,
  CreateOneOnOneApiResponse,
  UpdateOneOnOneApiRequest,
  UpdateOneOnOneApiResponse,
  GetEmployeeOneOnOnesApiRequest,
  GetEmployeeOneOnOnesApiResponse,
  GetManagerOneOnOnesApiRequest,
  GetManagerOneOnOnesApiResponse,
  GetOneOnOneByIdApiResponse,
  CompleteStageApiResponse,
  GetManagerDirectEmployeesWithMeetingsApiResponse,
  GetProfessionalMeetingsApiRequest,
  GetProfessionalMeetingsAsManagerApiResponse,
  GetProfessionalMeetingsAsEmployeeApiResponse,
  GetUserAssignedGoalsApiResponse,
} from '@momentum/types';
import { OneOnOneStage } from '@momentum/types';

export const oneOnOneService = {
  async create(
    data: CreateOneOnOneApiRequest
  ): Promise<CreateOneOnOneApiResponse> {
    return (await apiClient.post('/one-on-one', data)).data;
  },

  async update(
    id: string,
    data: Omit<UpdateOneOnOneApiRequest, '_id'>
  ): Promise<UpdateOneOnOneApiResponse> {
    return (await apiClient.patch(`/one-on-one/${id}`, data)).data;
  },

  async getById(id: string): Promise<GetOneOnOneByIdApiResponse> {
    return (await apiClient.get(`/one-on-one/${id}`)).data;
  },

  async getByEmployeeId(
    params: GetEmployeeOneOnOnesApiRequest
  ): Promise<GetEmployeeOneOnOnesApiResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('employeeId', params.employeeId);

    if (params.status) {
      queryParams.append('status', params.status);
    }

    return (
      await apiClient.get(`/one-on-one/employee?${queryParams.toString()}`)
    ).data;
  },

  async getByManagerId(
    params: GetManagerOneOnOnesApiRequest
  ): Promise<GetManagerOneOnOnesApiResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('managerId', params.managerId);

    if (params.status) {
      queryParams.append('status', params.status);
    }

    return (
      await apiClient.get(`/one-on-one/manager?${queryParams.toString()}`)
    ).data;
  },

  async completeStage(
    oneOnOneId: string,
    stage: OneOnOneStage
  ): Promise<CompleteStageApiResponse> {
    return (
      await apiClient.post(`/one-on-one/${oneOnOneId}/complete-stage`, {
        stage,
      })
    ).data;
  },

  async getManagerDirectEmployeesWithMeetings(
    managerId: string
  ): Promise<GetManagerDirectEmployeesWithMeetingsApiResponse> {
    return (
      await apiClient.get(
        `/one-on-one/manager/direct-employees-with-meetings?managerId=${managerId}`
      )
    ).data;
  },

  async getProfessionalMeetingsAsManager(
    params: GetProfessionalMeetingsApiRequest
  ): Promise<GetProfessionalMeetingsAsManagerApiResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('employeeId', params.employeeId);

    return (
      await apiClient.get(
        `/one-on-one/professional/as-manager?${queryParams.toString()}`
      )
    ).data;
  },

  async getProfessionalMeetingsAsEmployee(
    params: GetProfessionalMeetingsApiRequest
  ): Promise<GetProfessionalMeetingsAsEmployeeApiResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('employeeId', params.employeeId);

    return (
      await apiClient.get(
        `/one-on-one/professional/as-employee?${queryParams.toString()}`
      )
    ).data;
  },

  async replaceOpen(
    data: CreateOneOnOneApiRequest
  ): Promise<CreateOneOnOneApiResponse> {
    return (await apiClient.post('/one-on-one/replace-open', data)).data;
  },

  async getUserAssignedGoals(
    employeeId: string,
    meetingType: string,
    managerId: string
  ): Promise<GetUserAssignedGoalsApiResponse> {
    const params = new URLSearchParams();
    params.append('employeeId', employeeId);
    params.append('meetingType', meetingType);
    params.append('managerId', managerId);
    const url = `/one-on-one/user/assigned-goals?${params.toString()}`;
    return (await apiClient.get(url)).data;
  },
};
